import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Calendar, Clock, CheckCircle, ArrowRight, Sparkles, BookOpen } from 'lucide-react';

const QUICK_OPTIONS = [
  { label: 'Less than 2 months', days: 45, desc: 'Intensive preparation' },
  { label: '2–4 months', days: 90, desc: 'Recommended pace' },
  { label: '4–6 months', days: 150, desc: 'Comfortable timeline' }
];

const MIN_PREP = { A2: 45, B1: 60, B2: 75 };

const ExamDateSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const level = searchParams.get('level') || 'B1';
  const [mode, setMode] = useState('quick');
  const [selectedOption, setSelectedOption] = useState(null);
  const [customDate, setCustomDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [result, setResult] = useState(null);

  const handleSave = async () => {
    let examDate;
    if (mode === 'quick' && selectedOption !== null) {
      examDate = new Date(Date.now() + QUICK_OPTIONS[selectedOption].days * 86400000);
    } else if (mode === 'calendar' && customDate) {
      examDate = new Date(customDate);
    } else {
      toast.error('Please select your exam date');
      return;
    }

    const now = new Date();
    if (examDate <= now) {
      toast.error('Exam date must be in the future');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/payments/set-exam-date', {
        exam_date: examDate.toISOString(),
        level
      });
      setResult(response.data);
      setSaved(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save exam date');
    } finally {
      setSaving(false);
    }
  };

  if (saved && result) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-3xl bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="study-plan-created-title">
            Your personalized DELF study plan has been created.
          </h1>
          <p className="text-muted-foreground mb-8">
            You have <span className="font-bold text-foreground">{result.days_until_exam} days</span> until your exam.
            Your access has been set to <span className="font-bold text-foreground">{result.access_days} days</span>.
          </p>

          <div className="bg-white rounded-2xl border border-border p-6 mb-8 text-left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold">DELF {level} Study Plan</p>
                <p className="text-xs text-muted-foreground">Exam: {new Date(result.exam_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span>Listening — 10 exercises</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span>Reading — 10 exercises</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span>Writing — 10 exercises</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span>Speaking — 10 exercises</span></div>
            </div>
          </div>

          <Button
            className="rounded-full px-8 py-5 font-bold text-[15px]"
            onClick={() => navigate(`/level/${level}`)}
            data-testid="start-studying-btn"
          >
            Start Studying Now <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  const minDate = new Date(Date.now() + MIN_PREP[level] * 86400000).toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full"
        data-testid="exam-date-selection-page"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="exam-date-title">
            When is your official DELF examination?
          </h1>
          <p className="text-muted-foreground">
            We'll personalize your {level} study plan and access period based on your exam date.
          </p>
        </div>

        {/* Quick options */}
        <div className="space-y-3 mb-6">
          {QUICK_OPTIONS.map((opt, i) => (
            <button
              key={i}
              onClick={() => { setMode('quick'); setSelectedOption(i); setCustomDate(''); }}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                mode === 'quick' && selectedOption === i
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/30'
              }`}
              data-testid={`exam-option-${i}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">{opt.label}</p>
                  <p className="text-xs text-muted-foreground">{opt.desc}</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  mode === 'quick' && selectedOption === i ? 'border-primary bg-primary' : 'border-gray-300'
                }`}>
                  {mode === 'quick' && selectedOption === i && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground uppercase font-medium">or select exact date</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Calendar input */}
        <div className="mb-8">
          <input
            type="date"
            min={minDate}
            value={customDate}
            onChange={e => { setCustomDate(e.target.value); setMode('calendar'); setSelectedOption(null); }}
            className={`w-full px-4 py-3.5 rounded-xl border-2 text-sm transition-all cursor-pointer ${
              mode === 'calendar' && customDate ? 'border-primary bg-primary/5' : 'border-border'
            } focus:outline-none focus:ring-2 focus:ring-primary/20`}
            data-testid="exam-date-calendar"
          />
          <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Minimum preparation: {MIN_PREP[level]} days for DELF {level}
          </p>
        </div>

        <Button
          className="w-full rounded-full py-5 font-bold text-[15px]"
          onClick={handleSave}
          disabled={saving || (mode === 'quick' && selectedOption === null) || (mode === 'calendar' && !customDate)}
          data-testid="save-exam-date-btn"
        >
          {saving ? 'Setting up your plan...' : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Create My Personalized Study Plan
            </>
          )}
        </Button>
      </motion.div>
    </div>
  );
};

export default ExamDateSelection;
